import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Avatar } from '../components/Avatar'
import { useChatThreads, useChatMessages, useCreateThread, useDeleteThread, useUpdateThread, useAddMessage, useVerifyPendingQuery, ChatMessage } from '../hooks/useChat'
import { useIsMobile } from '../hooks/useMediaQuery'
import VerdictSeal, {
  toneForConfidence,
  toneForVerdict,
  confidenceBand,
  CONFIDENCE_TEXT_CLASS,
  CONFIDENCE_BAND_LABEL,
  VERDICT_LABEL,
  BackendVerdict,
} from '../components/VerdictSeal'
import SourcingChip from '../components/SourcingChip'
import { VerdictFeedback } from '../components/VerdictFeedback'
import { shareApi } from '../services/api'
import { shareText } from '../utils/shareText'
import type { SourceReference } from '../types'


const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [inputText, setInputText] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-grow the verify input with its content, capped by the CSS max-height
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [inputText])
  // Desktop rail state — persisted. On mobile the rail is replaced by a drawer.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = localStorage.getItem('treu_sidebar_open')
    return stored ? JSON.parse(stored) : true
  })
  // Mobile drawer state — transient, always starts closed.
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [optimisticUserMessage, setOptimisticUserMessage] = useState<ChatMessage | null>(null)

  const [activeChatId, setActiveChatId] = useState<string>(() =>
    localStorage.getItem('treu_active_chat_id') || ''
  )
  const [expandedAnalysis, setExpandedAnalysis] = useState<Record<string, boolean>>({})
  // Per-message share state: 'busy' while minting, 'copied' for the 2s toast
  const [shareState, setShareState] = useState<Record<string, 'busy' | 'copied' | 'error'>>({})

  // React Query hooks for server state management
  const { data: chatThreads = [], isLoading: isLoadingThreads } = useChatThreads()
  const { data: chatMessages = [], isLoading: isLoadingMessages } = useChatMessages(activeChatId || null)
  const createThreadMutation = useCreateThread()
  const deleteThreadMutation = useDeleteThread()
  const updateThreadMutation = useUpdateThread()
  const addMessageMutation = useAddMessage()
  const verifyPendingMutation = useVerifyPendingQuery()

  // Build messages array with optimistic user message and typing indicator
  const messages: ChatMessage[] = [...chatMessages]

  if (optimisticUserMessage && !chatMessages.some(m => m.text === optimisticUserMessage.text && m.type === 'user')) {
    messages.push(optimisticUserMessage)
  }

  // Show typing indicator while sending a message or verifying
  if (addMessageMutation.isPending || verifyPendingMutation.isPending) {
    messages.push({
      id: 'typing',
      type: 'typing' as const,
      text: undefined,
      result: undefined,
      timestamp: new Date()
    })
  }

  const scrollViewRef = useRef<HTMLDivElement>(null)

  // Don't reset scroll on mount - let natural scroll behavior work
  // Removed the scroll reset that was causing content to be cut off

  // Set active chat when threads load - use stored chat if it exists, otherwise most recent
  useEffect(() => {
    if (!isLoadingThreads && chatThreads.length > 0) {
      // Check if current activeChatId exists in threads
      const chatExists = activeChatId && chatThreads.some(chat => chat.id === activeChatId)
      if (!chatExists) {
        const firstThread = chatThreads[0]
        if (firstThread) setActiveChatId(firstThread.id)
      }
    }
  }, [isLoadingThreads, chatThreads, activeChatId])

  const scrollToBottom = () => {
    if (scrollViewRef.current) {
      const lastMessage = scrollViewRef.current.lastElementChild
      if (lastMessage) {
        lastMessage.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    }
  }

  useEffect(() => {
    // Only scroll to bottom for new messages, not on initial load
    if (messages.length > 1) {
      scrollToBottom()
    }
  }, [messages.length])

  // Scroll to bottom when switching chats, when messages finish loading, or when new messages are added
  // This ensures the last message is visible above the input bar
  useEffect(() => {
    if (activeChatId && !isLoadingMessages && messages.length > 0) {
      // Small delay to ensure DOM is updated with new messages
      setTimeout(() => {
        scrollToBottom()
      }, 50)
    }
  }, [activeChatId, isLoadingMessages, messages.length])



  // Save sidebar state to localStorage — desktop preference only; a phone visit
  // must not clobber the persisted desktop rail state.
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('treu_sidebar_open', JSON.stringify(sidebarOpen))
    }
  }, [sidebarOpen, isMobile])

  // Save active chat ID to localStorage
  useEffect(() => {
    if (activeChatId) {
      localStorage.setItem('treu_active_chat_id', activeChatId)
    }
  }, [activeChatId])

  const addUserMessage = async (text: string, targetChatId?: string) => {
    let chatId = targetChatId || activeChatId

    // Fallback: if no active chat but threads exist, use the first one
    if (!chatId && chatThreads.length > 0) {
      const firstThread = chatThreads[0]
      if (firstThread) {
        chatId = firstThread.id
        setActiveChatId(chatId)
      }
    }

    if (!chatId) {
      console.error('Cannot send message: no active chat ID and no threads available')
      return
    }


    try {
      await addMessageMutation.mutateAsync({
        threadId: chatId,
        messageType: 'user',
        content: text
      })
      setTimeout(scrollToBottom, 0)
      return Date.now().toString() // Return temporary ID
    } catch (error) {
      console.error('Failed to add user message:', error)
      throw error // Let calling code handle the error
    }
  }

  const createNewChat = async (_userQuery?: string) => {
    try {
      const newThread = await createThreadMutation.mutateAsync()
      setActiveChatId(newThread.id)
      return newThread.id
    } catch (error) {
      console.error('Failed to create new chat:', error)
      throw error // Let calling code handle the error
    }
  }

  const deleteChat = async (chatId: string) => {
    if (chatThreads.length <= 1) {
      alert('Cannot delete the last remaining chat.')
      return
    }

    if (window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      try {
        await deleteThreadMutation.mutateAsync(chatId)

        // If deleting the active chat, switch to the first available chat
        if (activeChatId === chatId) {
          const remainingChats = chatThreads.filter(chat => chat.id !== chatId)
          const firstRemaining = remainingChats[0]
          if (firstRemaining) {
            setActiveChatId(firstRemaining.id)
          }
        }
      } catch (error) {
        console.error('Failed to delete chat:', error)
        alert('Failed to delete chat. Please try again.')
      }
    }
  }

  // Filter chats based on search query
  const filteredChats = chatThreads.filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    const text = inputText.trim()
    if (!text || addMessageMutation.isPending) {
      return
    }

    // Clear input and show optimistic user bubble immediately
    setInputText('')
    setOptimisticUserMessage({
      id: `optimistic-${Date.now()}`,
      type: 'user' as const,
      text,
      result: undefined,
      timestamp: new Date()
    })

    let chatIdToUse = activeChatId
    let isNewChat = false

    // If no active chat or the active chat doesn't exist in the current threads, create a new one
    if (!chatIdToUse || !chatThreads.find(chat => chat.id === chatIdToUse)) {
      try {
        chatIdToUse = await createNewChat()
        isNewChat = true
      } catch (error) {
        console.error('Failed to create new chat:', error)
        setOptimisticUserMessage(null)
        setInputText(text)
        return
      }
    }

    // Update title for new chats or chats with default title
    const currentChat = chatThreads.find(chat => chat.id === chatIdToUse)
    if (isNewChat || (currentChat && (currentChat.title === 'New Chat' || currentChat.title === 'New Verification'))) {
      const title = text.length > 30 ? text.substring(0, 30) + '...' : text
      try {
        await updateThreadMutation.mutateAsync({ threadId: chatIdToUse, title })
      } catch (error) {
        console.error('Failed to update chat title:', error)
      }
    }

    // Send to backend — optimistic bubble already visible
    try {
      await addUserMessage(text, chatIdToUse)
      setOptimisticUserMessage(null)
    } catch (error) {
      console.error('Failed to send message:', error)
      setOptimisticUserMessage(null)
      alert('Failed to send message. Please try again.')
    }
  }

  // Verdict presentation — displays exactly what the engine produced:
  // the verdict word (support-for-claim scale) and the confidence score
  // (how clearly the claim was resolved — high even for refutations).
  // No derived/inverted numbers.
  const TONE_TEXT_CLASS = {
    true: 'text-verdict-true',
    mixed: 'text-verdict-mixed',
    unverified: 'text-verdict-unverified',
    false: 'text-verdict-false',
  } as const

  const clearShareState = (key: string) =>
    setShareState(prev => { const next = { ...prev }; delete next[key]; return next })

  const handleShareVerdict = async (messageId: string, result: { id: string; headline?: string; verdict?: string }) => {
    setShareState(prev => ({ ...prev, [messageId]: 'busy' }))
    try {
      const { url } = await shareApi.createShare(result.id)
      const text = shareText(result.headline, result.verdict as BackendVerdict | undefined, url)
      if (navigator.share) {
        await navigator.share({ text })
        clearShareState(messageId)
      } else {
        await navigator.clipboard.writeText(text)
        setShareState(prev => ({ ...prev, [messageId]: 'copied' }))
        setTimeout(() => clearShareState(messageId), 2000)
      }
    } catch (error) {
      // AbortError = user closed the native share sheet; not an error state
      if ((error as Error)?.name === 'AbortError') {
        clearShareState(messageId)
        return
      }
      setShareState(prev => ({ ...prev, [messageId]: 'error' }))
      setTimeout(() => clearShareState(messageId), 2000)
    }
  }

  // "Copy link" puts ONLY the URL on the clipboard — for pasting into a
  // browser, doc, or anywhere the sentence would be in the way.
  const handleCopyLink = async (messageId: string, result: { id: string }) => {
    const key = `${messageId}:link`
    setShareState(prev => ({ ...prev, [key]: 'busy' }))
    try {
      const { url } = await shareApi.createShare(result.id)
      await navigator.clipboard.writeText(url)
      setShareState(prev => ({ ...prev, [key]: 'copied' }))
    } catch {
      setShareState(prev => ({ ...prev, [key]: 'error' }))
    }
    setTimeout(() => clearShareState(key), 2000)
  }

  const getVerdictPresentation = (result: { verdict?: string; confidence: number }) => {
    const verdict = result.verdict as BackendVerdict | undefined
    if (verdict && verdict in VERDICT_LABEL) {
      const tone = toneForVerdict(verdict)
      return {
        tone,
        score: result.confidence,
        label: VERDICT_LABEL[verdict],
        hasVerdict: true,
        textClass: TONE_TEXT_CLASS[tone],
      }
    }
    const tone = toneForConfidence(result.confidence)
    return {
      tone,
      score: result.confidence,
      label: 'Confidence in this analysis',
      hasVerdict: false,
      textClass: TONE_TEXT_CLASS[tone],
    }
  }

  // Get user wisdom level
  const getUserWisdomLevel = () => {
    const verifications = user?.monthlyChecksUsed || user?.trialChecksUsed || 0
    const reputation = user?.reputationScore || 0
    const level = Number(user?.communityLevel) || 1

    if (verifications >= 500 || reputation >= 1000 || level >= 5) return { name: 'Wisdom Curator', icon: '♦', level: 5 }
    if (verifications >= 201 || reputation >= 500 || level >= 4) return { name: 'Discerning Mind', icon: '♠', level: 4 }
    if (verifications >= 51 || reputation >= 200 || level >= 3) return { name: 'Critical Thinker', icon: '♣', level: 3 }
    if (verifications >= 10 || reputation >= 50 || level >= 2) return { name: 'Truth Seeker', icon: '♦', level: 2 }
    return { name: 'Curious Observer', icon: '♥', level: 1 }
  }

  // Wisdom level available for future use
  void getUserWisdomLevel

  const closeDrawer = () => setDrawerOpen(false)

  // Search + thread list + new-verification button (shared by desktop rail and mobile drawer)
  const sidebarBody = (
    <>
      <div className="px-4 pb-3">
        <input
          type="text"
          placeholder="Search verifications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 text-sm text-white bg-white/10 border border-white/15 rounded-sm outline-none placeholder:text-white/50 focus:border-accent-300/60 transition-colors duration-150"
        />
      </div>

      <div className="flex-1 overflow-y-auto px-2">
        {filteredChats.map((chat) => (
          <button
            key={chat.id}
            onClick={() => {
              setActiveChatId(chat.id)
              closeDrawer()
            }}
            className={`w-full text-left p-3 mb-1 rounded-md flex items-center justify-between transition-colors duration-150 border ${
              activeChatId === chat.id
                ? 'bg-white/15 border-white/20'
                : 'bg-transparent border-transparent hover:bg-white/10'
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white mb-1 overflow-hidden whitespace-nowrap text-ellipsis">
                {chat.title}
              </div>
              <div className="text-xs text-white/60">
                {chat.updatedAt.toLocaleDateString()}
              </div>
            </div>

            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                deleteChat(chat.id)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  deleteChat(chat.id)
                }
              }}
              className="w-6 h-6 ml-2 flex-shrink-0 flex items-center justify-center rounded text-white/50 hover:text-white hover:bg-danger-500/40 transition-colors duration-150"
              title="Delete chat"
            >
              ×
            </span>
          </button>
        ))}

        {filteredChats.length === 0 && searchQuery && (
          <div className="p-4 text-center text-white/60 text-sm">
            No chats found matching &quot;{searchQuery}&quot;
          </div>
        )}
      </div>

      <div className="p-4 border-t border-white/10">
        <button
          onClick={() => {
            createNewChat()
            closeDrawer()
          }}
          className="w-full px-4 py-3 bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium rounded-md transition-colors duration-150"
        >
          + New Verification
        </button>
      </div>
    </>
  )

  return (
    <div className="bg-paper-0 min-h-[100dvh] relative">
      {/* ── Desktop sidebar rail (hidden on mobile) ─────────────── */}
      <div
        className={`hidden md:flex flex-col fixed right-0 top-0 bottom-0 pt-[84px] z-50 bg-brand-950 border-l border-white/10 transition-[width] duration-300 ease-out ${
          sidebarOpen ? 'w-[300px]' : 'w-[60px]'
        }`}
      >
        <div className={`p-4 flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
          {sidebarOpen && (
            <span className="font-display text-base font-semibold text-white">
              Verifications
            </span>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded text-white hover:bg-white/10 transition-colors duration-150 text-xl leading-none"
            title={sidebarOpen ? 'Collapse history' : 'Expand history'}
          >
            {sidebarOpen ? '→' : '←'}
          </button>
        </div>
        {sidebarOpen && sidebarBody}
      </div>

      {/* ── Mobile drawer + backdrop ────────────────────────────── */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-[105] bg-black/40"
          onClick={closeDrawer}
          aria-hidden="true"
        />
      )}
      <div
        className={`md:hidden flex flex-col fixed inset-y-0 right-0 z-[110] w-[85vw] max-w-[320px] bg-brand-950 shadow-overlay transition-transform duration-300 ease-out ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Verification history"
      >
        <div className="p-4 flex items-center justify-between">
          <span className="font-display text-base font-semibold text-white">
            Verifications
          </span>
          <button
            onClick={closeDrawer}
            className="w-10 h-10 flex items-center justify-center rounded text-white hover:bg-white/10 transition-colors duration-150 text-xl leading-none"
            title="Close history"
          >
            ×
          </button>
        </div>
        {sidebarBody}
      </div>

      {/* ── Mobile history toggle (floating, below header) ──────── */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="md:hidden fixed top-[76px] right-4 z-40 flex items-center gap-1.5 px-3 py-2 bg-brand-950 text-white text-xs font-medium rounded-md shadow-card border border-white/10"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        History
      </button>

      {/* ── Main chat area ───────────────────────────────────────── */}
      <div
        className={`pt-4 pb-4 transition-[padding] duration-300 ease-out ${
          sidebarOpen ? 'md:pr-[300px]' : 'md:pr-[60px]'
        }`}
      >
        <div className="mx-auto w-full max-w-[700px] px-4">
          <div ref={scrollViewRef} className="flex flex-col gap-4 w-full">
            {isLoadingMessages && activeChatId ? (
              <div className="flex justify-center items-center p-8 text-ink-600">
                <div className="flex gap-1 items-center">
                  <div className="w-2 h-2 rounded-full bg-ink-300" style={{ animation: 'pulse 1.4s ease-in-out infinite' }} />
                  <div className="w-2 h-2 rounded-full bg-ink-300" style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0.2s' }} />
                  <div className="w-2 h-2 rounded-full bg-ink-300" style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0.4s' }} />
                  <span className="ml-2 text-sm">Loading messages...</span>
                </div>
              </div>
            ) : messages.map((message) => {
              if (message.type === 'user') {
                // User messages: right-aligned
                return (
                  <div key={message.id} className="w-full flex justify-end items-start gap-3">
                    <div className="bg-brand-950 text-white p-4 rounded-lg rounded-tr-sm max-w-[85%] md:max-w-[500px] break-words">
                      {message.text && (
                        <div className="mb-2">
                          {message.text.split('\n').map((line, index) => (
                            <div key={index} className={index < message.text!.split('\n').length - 1 ? 'mb-2' : ''}>
                              {line.startsWith('**') && line.endsWith('**') ? (
                                <strong>{line.slice(2, -2)}</strong>
                              ) : (
                                line
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="text-xs text-white/60 mt-2 text-right">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <div className="hidden md:block flex-shrink-0">
                      <Avatar size="medium" />
                    </div>
                  </div>
                );
              } else {
                // System messages: left-aligned
                return (
                  <div key={message.id} className="w-full flex justify-start">
                    <div className="bg-white text-ink-700 p-4 rounded-lg rounded-tl-sm border border-paper-200 shadow-card max-w-[95%] md:max-w-[600px]">
                      {message.type === 'typing' ? (
                        <div className="w-[240px] max-w-full py-1">
                          <div className="h-1 rounded-full bg-paper-100 overflow-hidden">
                            <div
                              className="h-full w-1/4 rounded-full bg-accent-500"
                              style={{ animation: 'tr-scan 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite' }}
                            />
                          </div>
                          <p className="mt-3 text-ink-400 text-sm">Cross-referencing trusted sources…</p>
                        </div>
                      ) : (
                        <div>
                          {(message.result?.type === 'trial_limit_exceeded' || message.result?.type === 'monthly_limit_exceeded') ? (
                            <div>
                              <div className="mb-3">
                                {user?.subscriptionTier === 'free'
                                  ? message.text
                                  : 'You can now verify this query with your upgraded plan.'}
                              </div>
                              {user?.subscriptionTier === 'free' ? (
                                <button
                                  onClick={() => navigate('/profile#upgrade')}
                                  className="bg-accent-500 hover:bg-accent-600 text-white px-4 py-2 rounded-sm font-medium text-sm transition-colors duration-150"
                                >
                                  Upgrade
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    verifyPendingMutation.mutate({
                                      threadId: activeChatId,
                                      messageId: message.id
                                    })
                                  }}
                                  disabled={verifyPendingMutation.isPending}
                                  className="bg-accent-500 hover:bg-accent-600 text-white px-4 py-2 rounded-sm font-medium text-sm transition-colors duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                  {verifyPendingMutation.isPending ? 'Verifying...' : 'Verify this'}
                                </button>
                              )}
                            </div>
                          ) : (() => {
                            // A follow-up elaboration renders as a plain conversational reply —
                            // no headline, no confidence badge, no sources, no analysis expander.
                            const isElaboration = message.result?.kind === 'elaboration'
                            const isVerification = !!message.result && message.result.type !== 'trial_limit_exceeded' && !isElaboration
                            const leadText = isVerification
                              ? (message.result?.shortAnswer ?? message.text)
                              : message.text
                            return leadText ? (
                              <div className={message.result ? 'mb-4' : ''}>
                                {/* The Verdict Seal leads a verification turn */}
                                {isVerification && (() => {
                                  const v = getVerdictPresentation(message.result!)
                                  return (
                                    <div className="flex items-center gap-4 mb-4">
                                      <VerdictSeal confidence={v.score} tone={v.tone} size={56} />
                                      <div className="min-w-0 flex-1">
                                        {message.result?.headline && (
                                          <div className="font-display font-semibold text-ink-900 leading-snug">
                                            {message.result.headline}
                                          </div>
                                        )}
                                        <div className={`text-xs font-medium mt-1 ${v.textClass}`}>
                                          {v.hasVerdict && `${v.label} of the claim · `}
                                          <span className={CONFIDENCE_TEXT_CLASS[confidenceBand(v.score)]} title={CONFIDENCE_BAND_LABEL[confidenceBand(v.score)]}>
                                            {v.score}% confidence in our analysis
                                          </span>
                                        </div>
                                      </div>
                                      {message.result?.id && (
                                        <div className="self-start flex-shrink-0 flex gap-1.5">
                                          <button
                                            onClick={() => handleShareVerdict(message.id, message.result!)}
                                            disabled={shareState[message.id] === 'busy'}
                                            title="Share this verdict"
                                            className="px-2.5 py-1 rounded-full border border-paper-200 bg-paper-50 text-xs text-ink-600 cursor-pointer hover:border-accent-500 hover:text-accent-600 transition-colors duration-150 disabled:opacity-50"
                                          >
                                            {shareState[message.id] === 'copied'
                                              ? 'Link copied'
                                              : shareState[message.id] === 'error'
                                                ? 'Couldn’t share'
                                                : 'Share'}
                                          </button>
                                          <button
                                            onClick={() => handleCopyLink(message.id, message.result!)}
                                            disabled={shareState[`${message.id}:link`] === 'busy'}
                                            title="Copy only the link"
                                            className="px-2.5 py-1 rounded-full border border-paper-200 bg-paper-50 text-xs text-ink-600 cursor-pointer hover:border-accent-500 hover:text-accent-600 transition-colors duration-150 disabled:opacity-50"
                                          >
                                            {shareState[`${message.id}:link`] === 'copied'
                                              ? 'Copied'
                                              : shareState[`${message.id}:link`] === 'error'
                                                ? 'Failed'
                                                : 'Copy link'}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })()}
                                {leadText.split('\n').map((line, index) => (
                                  <div key={index} className={index < leadText.split('\n').length - 1 ? 'mb-2' : ''}>
                                    {line.startsWith('**') && line.endsWith('**') ? (
                                      <strong>{line.slice(2, -2)}</strong>
                                    ) : (
                                      line
                                    )}
                                  </div>
                                ))}
                                {/* Live web-search chip: the score came from a current grounded search */}
                                {isVerification && message.result?.confidenceBasis === 'web_search' && (
                                  <SourcingChip kind="web_search" knowledgeBasis={message.result?.knowledgeBasis ?? undefined} />
                                )}
                                {/* Honest-sourcing chip when the score came from AI knowledge */}
                                {isVerification && message.result?.confidenceBasis === 'ai_knowledge' && (
                                  <SourcingChip kind="ai_knowledge" knowledgeBasis={message.result?.knowledgeBasis ?? undefined} />
                                )}
                                {/* Recency caveat when a time-sensitive claim wasn't backed by a live search */}
                                {isVerification && message.result?.confidenceBasis === 'sources' && message.result?.knowledgeBasis && (
                                  <div className="inline-block mt-2 mr-2 px-2 py-0.5 bg-verdict-unverified-bg border border-verdict-unverified/20 rounded-full text-[0.6875rem] text-verdict-unverified">
                                    {message.result.knowledgeBasis}
                                  </div>
                                )}
                              </div>
                            ) : null
                          })()}

                          {message.result && message.result.type !== 'trial_limit_exceeded' && message.result.kind !== 'elaboration' && (
                            <div>
                              {/* Full analysis — collapsed by default, instant (already generated) */}
                              {(message.result.fullAnalysis ?? message.result.reasoning) && (
                                <div className="mb-3">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedAnalysis(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                                    aria-expanded={!!expandedAnalysis[message.id]}
                                    className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-accent-300/60 bg-accent-100/60 px-3.5 py-1.5 font-medium text-sm text-accent-600 hover:border-accent-500 hover:bg-accent-100 transition-colors duration-150"
                                  >
                                    {expandedAnalysis[message.id] ? 'Hide full analysis' : 'Show full analysis'}
                                    <svg
                                      className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedAnalysis[message.id] ? 'rotate-180' : ''}`}
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth={2.5}
                                      viewBox="0 0 24 24"
                                      aria-hidden="true"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {expandedAnalysis[message.id] && (
                                    <div className="mt-2 text-sm text-ink-700 whitespace-pre-wrap">
                                      {message.result.fullAnalysis ?? message.result.reasoning}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Sources — the credentials behind the verdict, always visible */}
                              {message.result.sources && message.result.sources.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-paper-200">
                                  <div className="font-display text-[0.65rem] font-medium uppercase tracking-[0.18em] text-ink-400 mb-2">
                                    Sources ({message.result.sources.length})
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {message.result.sources.map((source: SourceReference, idx: number) => (
                                      <a
                                        key={idx}
                                        href={source.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={source.title}
                                        className="inline-flex items-center gap-1.5 max-w-full rounded-full bg-paper-50 border border-paper-200 px-3 py-1.5 text-xs text-ink-600 no-underline hover:border-accent-500 hover:text-accent-600 transition-colors duration-150"
                                      >
                                        <span className="truncate max-w-[220px]">{source.title}</span>
                                        <span className="font-medium text-ink-400 flex-shrink-0">· {source.relevance}</span>
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {message.result?.id
                            && message.result.type !== 'trial_limit_exceeded'
                            && message.result.type !== 'monthly_limit_exceeded'
                            && message.result.kind !== 'elaboration' && (
                            <VerdictFeedback factCheckId={message.result.id} messageId={message.id} />
                          )}

                          <div className="text-xs text-ink-400 mt-2 text-left">
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
            })}
            {/* Spacer element to ensure last message is visible above fixed input bar */}
            <div className="h-[120px] flex-shrink-0" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* ── Fixed chat input ─────────────────────────────────────── */}
      <div
        className={`fixed bottom-0 left-0 right-0 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2 transition-[right] duration-300 ease-out ${
          sidebarOpen ? 'md:right-[300px]' : 'md:right-[60px]'
        }`}
      >
        <div className="mx-auto w-full max-w-[700px] px-4">
          <form
            onSubmit={handleSendMessage}
            className="flex items-end gap-3 p-3 bg-brand-950 rounded-xl border border-white/10 shadow-overlay"
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  e.currentTarget.form?.requestSubmit()
                }
              }}
              placeholder="Type something you'd like me to verify..."
              disabled={false}
              className="flex-1 min-w-0 px-4 py-3 text-base leading-6 text-white bg-white/10 border-none outline-none rounded-md placeholder:text-white/60 focus:bg-white/15 transition-colors duration-150 resize-none max-h-48 overflow-y-auto"
            />
            <button
              type="submit"
              disabled={!inputText.trim()}
              className="px-4 md:px-6 py-3 bg-transparent border-none text-sm font-semibold text-white transition-opacity duration-150 hover:opacity-80 disabled:text-white/50 disabled:cursor-not-allowed disabled:hover:opacity-100"
            >
              Verify
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
